/**
 * <product-card-inline-options>
 *
 * Inline color + size selector for product cards. Supports four render modes:
 *   - 'both'           Color + Size, full overlay
 *   - 'color_only'     Color swatches + ADD, no size row
 *   - 'size_only'      Size pills + ADD, no swatches
 *   - 'single_variant' Just ADD (no options to pick — variant ID is pre-set)
 *
 * The render mode is set by the Liquid snippet via the `render-mode` attribute.
 * Variant availability is computed dynamically based on the current selection
 * AND the current render mode (e.g. in 'size_only' mode, color is not part of
 * the match).
 *
 * After a successful add-to-cart, refreshes the cart drawer via Shopify's
 * Section Rendering API and opens it.
 */

const CART_DRAWER_SECTION = 'cart-drawer';
const CART_DRAWER_SELECTOR = 'cart-drawer';

class ProductCardInlineOptions extends HTMLElement {
  constructor() {
    super();
    this.variants = [];
    this.selectedColor = null;
    this.selectedSize = null;
    this.renderMode = this.getAttribute('render-mode') || 'both';
    this.colorPosition = parseInt(this.getAttribute('color-position'), 10) || 0;
    this.sizePosition = parseInt(this.getAttribute('size-position'), 10) || 0;
  }

  connectedCallback() {
    const dataEl = this.querySelector('.product-card-inline-options__data');
    if (!dataEl) return;
    try {
      this.variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.warn('[product-card-inline-options] Failed to parse variant data', e);
      return;
    }

    this.colorButtons = Array.from(this.querySelectorAll('[data-color-value]'));
    this.sizeButtons = Array.from(this.querySelectorAll('[data-size-value]'));
    this.addButton = this.querySelector('[data-add-button]');
    this.variantInput = this.querySelector('[data-selected-variant-id]');
    this.form = this.querySelector('form');
    this.productCard = this.closest('product-card');

    // Pre-select first color if there's a color row.
    const firstColor = this.colorButtons[0];
    if (firstColor) {
      this.selectedColor = firstColor.dataset.colorValue;
    }

    this.bindEvents();

    // Initial state depends on mode.
    switch (this.renderMode) {
      case 'both':
      case 'size_only':
        // Need to compute which sizes are available before user picks.
        this.updateSizeAvailability();
        break;
      case 'color_only':
        // Color is pre-selected, so ADD becomes immediately actionable if
        // that color's variant is available.
        this.updateAddButton();
        break;
      case 'single_variant':
        // Variant ID is already hard-coded in the form by the Liquid snippet.
        // Enable the button only if the variant is available.
        if (this.variants[0] && this.variants[0].available) {
          this.addButton.disabled = false;
        }
        break;
    }
  }

  bindEvents() {
    this.colorButtons.forEach((btn) => {
      btn.addEventListener('click', () => this.handleColorClick(btn));
    });

    this.sizeButtons.forEach((btn) => {
      btn.addEventListener('click', () => this.handleSizeClick(btn));
    });

    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }
  }

  handleColorClick(btn) {
    const value = btn.dataset.colorValue;
    if (value === this.selectedColor) return;

    this.selectedColor = value;
    this.colorButtons.forEach((b) => {
      const isSelected = b === btn;
      b.classList.toggle('is-selected', isSelected);
      b.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    this.swapCardImage(value);

    if (this.renderMode === 'both') {
      this.updateSizeAvailability();
    } else if (this.renderMode === 'color_only') {
      // No size to gate on — the ADD button just maps directly to the picked color.
      this.updateAddButton();
    }
  }

  handleSizeClick(btn) {
    if (btn.disabled) return;
    const value = btn.dataset.sizeValue;
    this.selectedSize = value;
    this.sizeButtons.forEach((b) => {
      const isSelected = b === btn;
      b.classList.toggle('is-selected', isSelected);
      b.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });
    this.updateAddButton();
  }

  updateSizeAvailability() {
    if (this.renderMode === 'both' && !this.selectedColor) return;

    const availabilityBySize = {};
    this.variants.forEach((variant) => {
      const variantSize = variant.options[this.sizePosition - 1];

      if (this.renderMode === 'both') {
        // Match on color first.
        const variantColor = variant.options[this.colorPosition - 1];
        if (variantColor !== this.selectedColor) return;
      }
      // In 'size_only' mode there's no color to filter by, so every variant counts.

      availabilityBySize[variantSize] = variant.available === true;
    });

    let stillValid = false;
    this.sizeButtons.forEach((btn) => {
      const size = btn.dataset.sizeValue;
      const isAvailable = availabilityBySize[size] === true;
      btn.disabled = !isAvailable;
      btn.classList.toggle('is-disabled', !isAvailable);

      if (btn.classList.contains('is-selected')) {
        if (!isAvailable) {
          btn.classList.remove('is-selected');
          btn.setAttribute('aria-pressed', 'false');
        } else {
          stillValid = true;
        }
      }
    });

    if (!stillValid) {
      this.selectedSize = null;
    }
    this.updateAddButton();
  }

  updateAddButton() {
    const variant = this.resolveVariant();
    const ready = variant && variant.available;
    this.addButton.disabled = !ready;
    if (variant) {
      this.variantInput.value = variant.id;
    } else {
      this.variantInput.value = '';
    }
  }

  /**
   * Resolves the current selection to a single variant, based on render mode.
   * Returns null if the user hasn't made enough choices yet.
   */
  resolveVariant() {
    switch (this.renderMode) {
      case 'both':
        if (!this.selectedColor || !this.selectedSize) return null;
        return this.variants.find((v) => {
          return (
            v.options[this.colorPosition - 1] === this.selectedColor &&
            v.options[this.sizePosition - 1] === this.selectedSize
          );
        });

      case 'color_only':
        if (!this.selectedColor) return null;
        return this.variants.find(
          (v) => v.options[this.colorPosition - 1] === this.selectedColor
        );

      case 'size_only':
        if (!this.selectedSize) return null;
        return this.variants.find(
          (v) => v.options[this.sizePosition - 1] === this.selectedSize
        );

      case 'single_variant':
        return this.variants[0] || null;

      default:
        return null;
    }
  }

  swapCardImage(color) {
    if (!this.productCard) return;
    const variant = this.variants.find(
      (v) => v.options[this.colorPosition - 1] === color && v.featured_media_src
    );
    if (!variant || !variant.featured_media_src) return;

    const primaryImg = this.productCard.querySelector('.product-card__image--primary');
    if (!primaryImg) return;

    primaryImg.src = variant.featured_media_src;
    primaryImg.removeAttribute('srcset');
  }

  async handleSubmit(e) {
    e.preventDefault();
    const variant = this.resolveVariant();
    if (!variant || !variant.available) return;

    this.addButton.disabled = true;
    this.addButton.classList.add('is-loading');

    try {
      const formData = new FormData(this.form);
      formData.append('sections', CART_DRAWER_SECTION);
      formData.append('sections_url', window.location.pathname);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || error.message || 'Add to cart failed');
      }

      const result = await response.json();
      await this.refreshAndOpenDrawer(result);
    } catch (err) {
      console.warn('[product-card-inline-options] Add to cart failed', err);
      this.addButton.classList.add('has-error');
      setTimeout(() => this.addButton.classList.remove('has-error'), 1500);
    } finally {
      this.addButton.classList.remove('is-loading');
      this.updateAddButton();

      // For single_variant mode, updateAddButton would always set the button to
      // enabled (since the variant is fixed). After add, the cart may have
      // grown to its limit — re-enable defensively.
      if (this.renderMode === 'single_variant' && this.variants[0]?.available) {
        this.addButton.disabled = false;
      }
    }
  }

  async refreshAndOpenDrawer(addToCartResult) {
    const drawer = document.querySelector(CART_DRAWER_SELECTOR);
    if (!drawer) {
      console.warn('[product-card-inline-options] No <cart-drawer> found on page');
      return;
    }

    const sectionsHtml = addToCartResult.sections && addToCartResult.sections[CART_DRAWER_SECTION];

    if (sectionsHtml) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionsHtml, 'text/html');
      const freshDrawer = doc.querySelector(CART_DRAWER_SELECTOR);

      if (freshDrawer) {
        drawer.innerHTML = freshDrawer.innerHTML;
      }
    }

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

    if (typeof drawer.show === 'function') {
      drawer.show();
    } else if (typeof drawer.open === 'function') {
      drawer.open();
    } else {
      const trigger = document.querySelector('[aria-controls="cart-drawer"]');
      if (trigger) trigger.click();
    }
  }
}

if (!customElements.get('product-card-inline-options')) {
  customElements.define('product-card-inline-options', ProductCardInlineOptions);
}